"use client";

import { useState, type FormEvent } from "react";
import { Spinner } from "@/components/Spinner";
import { useRouter } from "next/navigation";
import { sendOtp, verifyOtp } from "@/lib/actions";

// Email-OTP signup for new customers → the builder. Same OTP path as login;
// Supabase Auth creates the account on first verify (shouldCreateUser: true).

const btn =
  "w-full rounded-full bg-stone-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-stone-700 disabled:opacity-50";
const field =
  "w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-stone-500 focus:outline-none";

export function RegisterFlow({
  next = "/create",
  initialEmail = "",
}: {
  next?: string;
  initialEmail?: string;
}) {
  const router = useRouter();
  const [stage, setStage] = useState<"email" | "code">("email");
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitEmail(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await sendOtp({ email });
    setBusy(false);
    if (!r.ok) return setError(r.error);
    setStage("code");
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const v = await verifyOtp({ email, code });
    setBusy(false);
    if (!v.ok) return setError(v.error);
    router.push(next);
  }

  return (
    <div className="mx-auto max-w-sm">
      {error && (
        <p className="mb-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>
      )}
      {stage === "email" ? (
        <form onSubmit={submitEmail} className="space-y-4 text-center">
          <input suppressHydrationWarning
            type="email"
            required
            autoFocus={!initialEmail}
            className={field}
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button type="submit" disabled={busy} className={btn}>
            {busy ? <Spinner tone="paper" label="Sending…" /> : "Create my account"}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} className="space-y-4 text-center">
          <p className="text-sm text-stone-600">
            We emailed a code to <span className="font-medium">{email}</span>.
          </p>
          <input suppressHydrationWarning
            inputMode="numeric"
            required
            autoFocus
            className={`${field} text-center text-lg tracking-[0.4em]`}
            placeholder="••••••"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 8))}
          />
          <button type="submit" disabled={busy} className={btn}>
            {busy ? <Spinner tone="ink" label="Verifying…" /> : "Verify & start building"}
          </button>
        </form>
      )}
    </div>
  );
}
